import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db, auth } from '../../firebase';

const ELEVATED = ['company_admin', 'developer', 'manager'];

function parseDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Compact recent company activity for Overview. Full log is under Dashboard → Activity.
 */
export default function TicketActivitySnippet({ userData, maxRows = 8, onOpenActivity, onOpenTickets }) {
  const companyId = userData?.companyId;
  const role = userData?.role;
  const uid = auth.currentUser?.uid;
  const wide = ELEVATED.includes(role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!companyId || !uid) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let q;
      if (wide) {
        q = query(
          collection(db, 'ticketLogs'),
          where('companyId', '==', companyId),
          orderBy('createdAt', 'desc'),
          limit(maxRows),
        );
      } else {
        q = query(
          collection(db, 'ticketLogs'),
          where('companyId', '==', companyId),
          where('participantUids', 'array-contains', uid),
          orderBy('createdAt', 'desc'),
          limit(maxRows),
        );
      }
      const snap = await getDocs(q);
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('TicketActivitySnippet:', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, uid, wide, maxRows]);

  useEffect(() => {
    load();
  }, [load]);

  if (!companyId) return null;

  return (
    <Accordion defaultExpanded={false} sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Recent activity</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Tickets, links, team changes, and other events your role can see.
          {onOpenActivity && (
            <>
              {' '}
              <Link component="button" type="button" variant="body2" onClick={onOpenActivity} sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}>
                Open activity log
              </Link>
            </>
          )}
          {onOpenTickets && (
            <>
              {onOpenActivity ? ' · ' : ' '}
              <Link component="button" type="button" variant="body2" onClick={onOpenTickets} sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}>
                Tickets
              </Link>
            </>
          )}
        </Typography>
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Who</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4}>Loading…</TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">No activity yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((log) => {
                const dt = parseDate(log.createdAt);
                return (
                  <TableRow key={log.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dt ? dt.toLocaleString() : '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.actorEmail || log.actorUid || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{log.actorRole}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={log.action} variant="outlined" /></TableCell>
                    <TableCell>{log.summary}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}
